import { useState, useEffect } from 'react';
import {
  Box, Button, Stack, Typography, CircularProgress,
  Paper, Alert, Chip, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  Download as DownloadIcon, Preview as PreviewIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { PLAMEService } from '../services/plameService';
import { PlanillonService } from '../../planillon/services/planillonService';
import type { PLAMEPreview } from '../types/plame';
import type { PlanillaCabecera } from '../../planillon/types/planillon';

export default function PLAMEPage() {
  const [planillas, setPlanillas] = useState<PlanillaCabecera[]>([]);
  const [selectedPlanilla, setSelectedPlanilla] = useState<number>(0);
  const [preview, setPreview] = useState<PLAMEPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPlanillas();
  }, []);

  const loadPlanillas = async () => {
    try {
      const data = await PlanillonService.getAll();
      const valid = data.filter(p => p.estado !== 'BORRADOR');
      setPlanillas(valid);
      if (valid.length > 0) setSelectedPlanilla(valid[0].idPlanilla);
    } catch { /* handled by interceptor */ }
  };

  const handlePreview = async () => {
    if (!selectedPlanilla) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const data = await PLAMEService.preview(selectedPlanilla);
      setPreview(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error generando preview PLAME');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!selectedPlanilla) return;
    setExporting(true);
    try {
      await PLAMEService.exportZip(selectedPlanilla);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error exportando PLAME');
    } finally {
      setExporting(false);
    }
  };

  const selectedInfo = planillas.find(p => p.idPlanilla === selectedPlanilla);

  const remColumns: GridColDef[] = [
    { field: 'tipoDocumento', headerName: 'Tipo Doc.', width: 80 },
    { field: 'numeroDocumento', headerName: 'Nro. Documento', width: 130 },
    { field: 'codigoConcepto', headerName: 'Cod. PDT', width: 100 },
    { field: 'monto', headerName: 'Monto', width: 120, type: 'number' },
  ];

  const jorColumns: GridColDef[] = [
    { field: 'tipoDocumento', headerName: 'Tipo Doc.', width: 80 },
    { field: 'numeroDocumento', headerName: 'Nro. Documento', width: 130 },
    { field: 'diasLaborados', headerName: 'Días Lab.', width: 90, type: 'number' },
    { field: 'diasNoLaborados', headerName: 'Días No Lab.', width: 100, type: 'number' },
    { field: 'diasSubsidiados', headerName: 'Días Sub.', width: 90, type: 'number' },
    { field: 'horasNormales', headerName: 'Hrs Norm.', width: 100 },
    { field: 'horasExtras', headerName: 'Hrs Extra', width: 100 },
  ];

  const fmt = (n: number) => `S/ ${n.toFixed(2)}`;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Exportación PLAME - SUNAT
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Selector de Planilla */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 350 }}>
            <InputLabel>Seleccionar Planilla</InputLabel>
            <Select
              value={selectedPlanilla || ''}
              label="Seleccionar Planilla"
              onChange={(e) => { setSelectedPlanilla(Number(e.target.value)); setPreview(null); }}
            >
              {planillas.map(p => (
                <MenuItem key={p.idPlanilla} value={p.idPlanilla}>
                  {p.descripcion} - {p.estado} ({p.cantidadTrabajadores} trab.)
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={18} /> : <PreviewIcon />}
            onClick={handlePreview}
            disabled={loading || !selectedPlanilla}
          >
            Vista Previa
          </Button>

          <Button
            variant="contained"
            color="success"
            startIcon={exporting ? <CircularProgress size={18} /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={exporting || !selectedPlanilla || !preview}
          >
            Descargar ZIP
          </Button>
        </Stack>

        {selectedInfo && (
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <Chip label={selectedInfo.estado} size="small" color="primary" />
            <Typography variant="body2" color="text.secondary">
              Rem: {fmt(selectedInfo.totalRemuneraciones)} | Desc: {fmt(selectedInfo.totalDescuentos)} | Aportes: {fmt(selectedInfo.totalAportes)}
            </Typography>
          </Stack>
        )}
      </Paper>

      {/* Preview Results */}
      {preview && (
        <>
          {/* Errores/Advertencias */}
          {preview.errores.length > 0 && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Advertencias ({preview.errores.length})</Typography>
              {preview.errores.map((err, i) => (
                <Typography key={i} variant="body2">- {err}</Typography>
              ))}
            </Alert>
          )}

          {/* Resumen */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Resumen de Exportación</Typography>
            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="body2" color="text.secondary">Periodo</Typography>
                <Typography variant="h6">{preview.periodo}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Trabajadores</Typography>
                <Typography variant="h6">{preview.totalTrabajadores}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Registros REM</Typography>
                <Typography variant="h6">{preview.totalRegistrosRem}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Registros JOR</Typography>
                <Typography variant="h6">{preview.totalRegistrosJor}</Typography>
              </Box>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Archivos: 0601{preview.periodo}.rem | 0601{preview.periodo}.jor
            </Typography>
          </Paper>

          {/* Muestra REM */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Muestra Archivo REM (Remuneraciones) - Primeros {preview.muestraRem.length} registros
          </Typography>
          <Box sx={{ mb: 3 }}>
            <DataGrid
              rows={preview.muestraRem.map((r, i) => ({ ...r, _id: i }))}
              columns={remColumns}
              getRowId={(row) => row._id}
              autoHeight
              density="compact"
              disableRowSelectionOnClick
              hideFooter
            />
          </Box>

          {/* Muestra JOR */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Muestra Archivo JOR (Jornada) - Primeros {preview.muestraJor.length} registros
          </Typography>
          <DataGrid
            rows={preview.muestraJor.map((r, i) => ({ ...r, _id: i }))}
            columns={jorColumns}
            getRowId={(row) => row._id}
            autoHeight
            density="compact"
            disableRowSelectionOnClick
            hideFooter
          />
        </>
      )}
    </Box>
  );
}
